import type { UserPayload, UserResponse } from '@/types/apps/user.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

export interface UserServiceInterface {}

type UserListResponse = ApiResponse<PaginatedResult<UserResponse>>;

export class UserService
    implements Showable, Updatable, Creatable, Indexable, Importable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        base: () => '/api/Admin/get-user-list',
        detail: (id: string) => `/api/Admin/get-user-detail/${id}`,
        create: () => '/api/Admin/create-user',
        resetPassword: () => '/api/Admin/reset-password',
        import: () => '/api/users/import',
        update: () => '/api/users',
        changePassword: () => '/api/users/change-password',
    };

    public async index(params?: QueryParams): Promise<UserListResponse> {
        return this.apiClient
            .fetchApi<
                PaginatedResult<UserResponse>
            >(this.endpoints.base(), params)
            .then(res => {
                return {
                    ...res,
                    result: {
                        ...res.result,
                        items: res.result.items.map(item => ({
                            ...item,
                            customName: `${item.email} - ${item.fullname}`,
                        })),
                    },
                };
            });
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<UserResponse>> {
        return this.apiClient.fetchApi(this.endpoints.detail(id), params);
    }
    public create(payload: UserPayload): Promise<ApiResponse<UserResponse>> {
        return this.apiClient.postApi(this.endpoints.create(), payload);
    }

    public update(
        id: string,
        payload: UserPayload
    ): Promise<ApiResponse<UserResponse>> {
        return this.apiClient.putApi(this.endpoints.update(), payload);
    }

    public resetPassword(payload: {
        userId: string;
        newPassword: string;
    }): Promise<ApiResponse<UserResponse>> {
        return this.apiClient.postApi(this.endpoints.resetPassword(), payload);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public changePassword(payload: {
        oldPassword: string;
        password: string;
    }): Promise<ApiResponse<UserResponse>> {
        return this.apiClient.putApi(this.endpoints.changePassword(), payload);
    }
}
